/**
 * MapStateContext for managing map data in the Needs Map
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';
import { Phase } from '@/types/phaseTypes';
import { Template, defaultTemplate } from '@/templates/defaultTemplate';
import { useMapLayout } from './MapLayoutContext';
import { sampleMapData } from '@/data/sampleData'; // サンプルデータをインポート

/**
 * Map data
 */
export interface MapData {
  id: string;
  name: string;
  nodes: TopicNode[];
  edges: TopicEdge[];
  transcript: string;
  phases: Phase[];
}

/**
 * MapStateContext type
 */
interface MapStateContextType {
  // State
  mapData: MapData | null;
  loading: boolean;
  error: Error | null;
  
  // Operations
  createMap: (name: string, transcript?: string) => MapData;
  updateNode: (nodeId: string, updates: Partial<TopicNode>) => void;
  updateEdge: (edgeId: string, updates: Partial<TopicEdge>) => void;
  updateNodePositions: (positions: Record<string, { x: number, y: number }>) => void;
  updateTranscript: (transcript: string) => void;
}

/**
 * Create MapStateContext
 */
const MapStateContext = createContext<MapStateContextType | undefined>(undefined);

/**
 * MapStateProvider props
 */
interface MapStateProviderProps {
  children: React.ReactNode;
  initialMapId?: string;
}

/**
 * MapStateProvider component
 */
export const MapStateProvider: React.FC<MapStateProviderProps> = ({ 
  children,
  initialMapId
}) => {
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  
  const { applyLayoutToTopicNodes } = useMapLayout();
  
  /**
   * Initialize map data from template
   */
  const initializeFromTemplate = useCallback(
    (template: Template = defaultTemplate, name: string = 'New Map', transcript: string = ''): MapData => {
      const id = `map_${Date.now()}`;
      
      // Apply initial layout to nodes
      const nodes = template.nodes.map(node => ({
        ...node,
        active: false, // 明示的に初期状態を設定
        contents: []
      }));
      
      const edges = template.edges.map(edge => ({
        ...edge,
        active: false, // 明示的に初期状態を設定
        contents: []
      }));
      
      const positionedNodes = applyLayoutToTopicNodes(nodes, edges, template.phases);
      
      return {
        id,
        name,
        nodes: positionedNodes,
        edges,
        transcript,
        phases: template.phases
      };
    },
    [applyLayoutToTopicNodes]
  );
  
  /**
   * Load map data
   */
  const loadMapData = useCallback(async (mapId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // In a real implementation, this would fetch from Firebase
      // For now, we'll just use the sample data
      if (mapId === 'sample_map_001' || mapId === sampleMapData.id) {
        // サンプルデータの使用
        const positionedNodes = applyLayoutToTopicNodes(
          sampleMapData.nodes, 
          sampleMapData.edges,
          defaultTemplate.phases // サンプルデータには存在しないのでテンプレートのフェーズを使用
        );
        
        setMapData({
          ...sampleMapData,
          nodes: positionedNodes,
          phases: defaultTemplate.phases // サンプルデータにフェーズ情報を追加
        });
      } else {
        // テンプレートからの新規作成
        const newMap = initializeFromTemplate(defaultTemplate, 'Sample Map');
        setMapData(newMap);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load map data'));
    } finally {
      setLoading(false);
    }
  }, [initializeFromTemplate, applyLayoutToTopicNodes]);
  
  /**
   * Create a new map
   */
  const createMap = useCallback(
    (name: string, transcript: string = '') => {
      const newMap = initializeFromTemplate(defaultTemplate, name, transcript);
      setMapData(newMap);
      return newMap;
    },
    [initializeFromTemplate]
  );
  
  /**
   * Update a node
   */
  const updateNode = useCallback((nodeId: string, updates: Partial<TopicNode>) => {
    setMapData(prevData => {
      if (!prevData) return null;
      
      const updatedNodes = prevData.nodes.map(node => 
        node.id === nodeId ? { ...node, ...updates } : node
      );
      
      return {
        ...prevData,
        nodes: updatedNodes
      };
    });
  }, []);
  
  /**
   * Update an edge
   */
  const updateEdge = useCallback((edgeId: string, updates: Partial<TopicEdge>) => {
    setMapData(prevData => {
      if (!prevData) return null;
      
      const updatedEdges = prevData.edges.map(edge => 
        edge.id === edgeId ? { ...edge, ...updates } : edge
      );
      
      return {
        ...prevData,
        edges: updatedEdges
      };
    });
  }, []);
  
  /**
   * Update node positions
   */
  const updateNodePositions = useCallback((nodePositions: Record<string, { x: number, y: number }>) => {
    setMapData(prevData => {
      if (!prevData) return null;
      
      const updatedNodes = prevData.nodes.map(node => {
        const newPosition = nodePositions[node.id];
        if (newPosition) {
          return {
            ...node,
            position: newPosition
          };
        }
        return node;
      });
      
      return {
        ...prevData,
        nodes: updatedNodes
      };
    });
  }, []);
  
  /**
   * Update transcript
   */
  const updateTranscript = useCallback((transcript: string) => {
    setMapData(prevData => {
      if (!prevData) return null;
      
      return {
        ...prevData,
        transcript
      };
    });
  }, []);
  
  /**
   * Load initial map data
   */
  useEffect(() => {
    if (initialMapId) {
      loadMapData(initialMapId);
    } else {
      // サンプルデータを読み込む
      const positionedNodes = applyLayoutToTopicNodes(
        sampleMapData.nodes, 
        sampleMapData.edges,
        defaultTemplate.phases // サンプルデータには存在しないのでテンプレートのフェーズを使用
      );
      
      setMapData({
        ...sampleMapData,
        nodes: positionedNodes,
        phases: defaultTemplate.phases // サンプルデータにフェーズ情報を追加
      });
      setLoading(false);
    }
  }, [initialMapId, loadMapData, applyLayoutToTopicNodes]);
  
  const value = {
    mapData,
    loading,
    error,
    createMap,
    updateNode,
    updateEdge,
    updateNodePositions,
    updateTranscript
  };
  
  return (
    <MapStateContext.Provider value={value}>
      {children}
    </MapStateContext.Provider>
  );
};

/**
 * useMapState hook
 */
export const useMapState = (): MapStateContextType => {
  const context = useContext(MapStateContext);
  
  if (context === undefined) {
    throw new Error('useMapState must be used within a MapStateProvider');
  }
  
  return context;
};